import { createSlice } from '@reduxjs/toolkit';

// Initial State
const initialState = {
  projectData: {},
  budgetData: [],
  teamData: [],
  selectedProjectId: null,
  selectedProject: null,
  invoiceData: [],
  settingsData: [],
  monthlyCostStatusData: {},
  revenueSpreadData: {},
  projects: [],
  companies: [],
  users: [],
  selectedCompany: null,
  overUnderData: [],
  showSettingsDialogBox: false,
  settingsDialogBoxTab: 0,
  monthlyCostStatusEmailFormData: null,
  monthlyCostStatusEmailFormFiles: [],
  monthlyCostStatusEmailFormSourceKey: null, // Which server copy the draft form was filled from
  viewMonthlyCostStatusSubmission: false, // Controls the visibility of the monthly cost status submission viewer
  viewMonthlyCostStatusEditor: false, // Controls the visibility of the monthly cost status editor
};

// Utility to generate simple setter reducers
const createSetter = key => (state, { payload }) => {
  state[key] = payload;
};

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setProjects: createSetter('projects'),
    setCompanies: createSetter('companies'),
    setSelectedCompany: createSetter('selectedCompany'),
    setProjectData: createSetter('projectData'),
    setUsers: createSetter('users'),
    setBudgetData: createSetter('budgetData'),
    setTeamData: createSetter('teamData'),
    setSelectedProjectId: createSetter('selectedProjectId'),
    setRevenueSpreadData: createSetter('revenueSpreadData'),
    setSelectedProject: (state, { payload }) => {
      state.selectedProject = payload;
      state.selectedProjectId = payload?.id || null; // Update selectedProjectId if payload has an id
      if (!payload?.id) {
        state.projectData = {};
        state.budgetData = [];  
        state.teamData = [];
        state.invoiceData = [];
        state.settingsData = [];
        state.monthlyCostStatusData = {};
        state.revenueSpreadData = {};
        state.overUnderData = [];
        state.showSettingsDialogBox = false;
        state.settingsDialogBoxTab = 0;
        state.monthlyCostStatusEmailFormData = null;
        state.monthlyCostStatusEmailFormFiles = [];
        state.monthlyCostStatusEmailFormSourceKey = null;
        state.viewMonthlyCostStatusSubmission = false; // Reset viewMonthlyCostStatusSubmission when no project is selected
        state.viewMonthlyCostStatusEditor = false; // Reset viewMonthlyCostStatusEditor when no project is selected
      }
    },
    setInvoiceData: createSetter('invoiceData'),
    setSettingsData: createSetter('settingsData'),
    setOverUnderData: createSetter('overUnderData'),
    setShowSettingsDialogBox: createSetter('showSettingsDialogBox'),
    setSettingsDialogBoxTab: createSetter('settingsDialogBoxTab'),
    setMonthlyCostStatusData: createSetter('monthlyCostStatusData'), // Stores the monthly cost status data received from the API
    setMonthlyCostStatusEmailFormData: createSetter('monthlyCostStatusEmailFormData'), // Stores the monthly cost status email draft data
    setMonthlyCostStatusEmailFormFiles: createSetter('monthlyCostStatusEmailFormFiles'), // Stores the files attached to the monthly cost status email draft
    setMonthlyCostStatusEmailFormSourceKey: createSetter('monthlyCostStatusEmailFormSourceKey'),
    setViewMonthlyCostStatusSubmission: createSetter('viewMonthlyCostStatusSubmission'), // Controls the visibility of the monthly cost status submission viewer
    setViewMonthlyCostStatusEditor: createSetter('viewMonthlyCostStatusEditor'), // Controls the visibility of the monthly cost status editor
    setMonthlyCostStatusEmailFormField: (state, { payload }) => {
      const { key, value } = payload;
      if (!state.monthlyCostStatusEmailFormData) state.monthlyCostStatusEmailFormData = {};
      state.monthlyCostStatusEmailFormData[key] = value;
    },
    mergeMonthlyCostStatusData: (state, { payload }) => {
      state.monthlyCostStatusData = { ...state.monthlyCostStatusData, ...payload };
    },
    // Reset all state to initial values
    resetProjectState: () => initialState,
  },
});

// Export actions and reducer
export const {
  setProjectData,
  setBudgetData,
  setTeamData,
  setSelectedProjectId,
  setRevenueSpreadData,
  resetProjectState,
  setSelectedProject,
  setInvoiceData,
  setSettingsData,
  setProjects,
  setCompanies,
  setUsers,
  setSelectedCompany,
  setOverUnderData,
  setShowSettingsDialogBox,
  setSettingsDialogBoxTab,
  setMonthlyCostStatusData,
  setMonthlyCostStatusEmailFormData,
  setMonthlyCostStatusEmailFormFiles,
  setMonthlyCostStatusEmailFormSourceKey,
  setViewMonthlyCostStatusSubmission,
  setViewMonthlyCostStatusEditor,
  setMonthlyCostStatusEmailFormField,
  mergeMonthlyCostStatusData,
} = projectSlice.actions;

export default projectSlice.reducer;
